import { ReactNode, useEffect, useMemo, useRef, useState } from 'react';

import { useFieldArray, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { FilterDropdownWrapper } from '@/components/atoms/FilterDropdownWrapper/FilterDropdownWrapper';
import { useAllInsights } from '@/components/pages/Insights/useInsights';
import useBrains from '@/hooks/useBrains';
import useDesks from '@/hooks/useDesks';
import useDialogs from '@/hooks/useDialogs';
import useMembers from '@/hooks/useMembers';
import { cn } from '@/lib/utils';
import useLanguageModels from '@/modules/developerTools/hooks/useLanguageModels';
import { providerNameMapper } from '@/modules/developerTools/pages/languageModels/helper';
import { ProviderName } from '@/modules/developerTools/types';
import { selectAvailableChannels } from '@/redux/integrations/selectors';
import { selectBrainId, selectDialogId } from '@/redux/session/selectors';
import { InsightFilter as InsightFilterType } from '@/shared/types/analytics';
import { INSIGHT_TYPES } from '@/shared/types/insights';
import { IntegrationType } from '@/shared/types/integration';
import { getChannelKey } from '@/util/channelName';
import { getFilterTitle } from '@/util/filterFormatters';
import { isKeyEnter } from '@/util/util';

import { ChannelsFilter } from './ChannelsFilter';
import AccountTagsFilter from './filters/AccountTagsFilter';
import { AgentsFilter } from './filters/AgentsFilter';
import { AIAgentGoalFilter } from './filters/AIAgentGoalFilter';
import { BrainsFilter } from './filters/BrainsFilter';
import { ChannelUserIdFilter } from './filters/ChannelUserIdFilter';
import { CollectionsRecalledFilter } from './filters/CollectionsRecalledFilter';
import { ContainmentFilter } from './filters/ContainmentFilter';
import { ConversationIdFilter } from './filters/ConversationIdFilter';
import { CoverageFilter } from './filters/CoverageFilter';
import { CsatStarPicker } from './filters/CsatStarPicker';
import { EnvironmentsFilter } from './filters/EnvironmentsFilter';
import { ExternalUserIdFilter } from './filters/ExternalUserIdFilter';
import { createSearchMatcher } from './filters/filterSearch';
import {
  brainSearchTerms,
  deskSearchTerms,
  languageModelSearchTerms,
  memberSearchTerms,
} from './filters/filterSearchTerms';
import { FirstMessageTypeFilter } from './filters/FirstMessageTypeFilter';
import { InsightFilter } from './filters/InsightFilter';
import { IntegrationsFilter } from './filters/IntegrationsFilter';
import { LanguageModelsFilter } from './filters/LanguageModelsFilter';
import { MeaningfulFilter } from './filters/MeaningfulFilter';
import { mergeAgentIds, mergeIds } from './filters/mergeSelection';
import { PreviewConversationsFilter } from './filters/PreviewConversationsFilter';
import { ReactionsFilter } from './filters/ReactionsFilter';
import { TagsFilter } from './filters/TagsFilter';
import { FilterPanelProps } from './filters/types';
import { UserEmailFilter } from './filters/UserEmailFilter';
import { UserIdFilter } from './filters/UserIdFilter';
import { UserMessagesFilter } from './filters/UserMessagesFilter';
import { UserNameFilter } from './filters/UserNameFilter';
import { UserSentimentFilter } from './filters/UserSentimentFilter';
import { VoicemailFilter } from './filters/VoicemailFilter';
import { VoiceRecordingsFilter } from './filters/VoiceRecordingsFilter';

const DEFAULT_WIDTH = 'w-[280px]';

type FilterLayout = 'compact' | 'roomy' | 'input';

interface FilterDefinition {
  Component?: React.ComponentType<FilterPanelProps>;
  layout: FilterLayout;
  bulkSelectable?: boolean;
  width?: string;
}

const FILTER_REGISTRY: Record<string, FilterDefinition> = {
  channels: { Component: ChannelsFilter, layout: 'roomy', bulkSelectable: true },
  tags: { Component: TagsFilter, layout: 'roomy' },
  environments: { Component: EnvironmentsFilter, layout: 'roomy', bulkSelectable: true },
  deskIds: { Component: EnvironmentsFilter, layout: 'roomy', bulkSelectable: true },
  integrationIds: { Component: IntegrationsFilter, layout: 'roomy', width: 'w-[360px]' },
  brainIds: { Component: BrainsFilter, layout: 'roomy', bulkSelectable: true },
  agentIds: { Component: AgentsFilter, layout: 'roomy', bulkSelectable: true },
  'language-models': { Component: LanguageModelsFilter, layout: 'roomy', bulkSelectable: true },
  goalAchieved: { Component: AIAgentGoalFilter, layout: 'compact', bulkSelectable: true },
  userSentiment: { Component: UserSentimentFilter, layout: 'compact', bulkSelectable: true },
  hasCollection: { Component: CollectionsRecalledFilter, layout: 'compact' },
  hasRecording: { Component: VoiceRecordingsFilter, layout: 'compact' },
  hasVoicemail: { Component: VoicemailFilter, layout: 'compact' },
  isTest: { Component: PreviewConversationsFilter, layout: 'compact' },
  isCovered: { Component: CoverageFilter, layout: 'compact' },
  isContained: { Component: ContainmentFilter, layout: 'compact' },
  isMeaningful: { Component: MeaningfulFilter, layout: 'compact' },
  reactions: { Component: ReactionsFilter, layout: 'compact' },
  firstMessageType: { Component: FirstMessageTypeFilter, layout: 'compact' },
  userName: { Component: UserNameFilter, layout: 'input' },
  userId: { Component: UserIdFilter, layout: 'input' },
  userEmail: { Component: UserEmailFilter, layout: 'input' },
  sessionId: { Component: ConversationIdFilter, layout: 'input' },
  externalUserId: { Component: ExternalUserIdFilter, layout: 'input' },
  channelUserId: { Component: ChannelUserIdFilter, layout: 'input' },
  minNumUserMessages: { Component: UserMessagesFilter, layout: 'roomy' },
  ratings: { layout: 'roomy', width: 'w-[367px]' },
  csatBrain: { layout: 'roomy', width: 'w-[367px]' },
};

interface SessionFilterDropdownProps {
  type: string;
  filterIndex: number;
  onClose: (source?: 'back' | 'outside') => void;
  onApply: () => void;
  onClear: () => void;
  variant?: 'floating' | 'popover';
  className?: string;
  scope?: 'brain' | 'account';
}

interface SelectAllHandler {
  type: string;
  filterIndex: number;
  update: (index: number, value: Record<string, unknown>) => void;
  watch: (name: string) => Record<string, unknown>;
  availableChannels?: string[];
  desks?: Array<{ desk_id: string; name: string }>;
  brains?: Array<{ brain_id: string; name: string }>;
  members?: Array<{ user_id: string; name: string; email: string }>;
  dialogTags?: Set<string>;
  enabledModelsWithMoveo?: Array<{ language_model_id: string; model: string; provider: string }>;
  insight?: { name: string; type: string; categories?: string[] };
  searchText?: string;
  channelLabel?: (channel: string) => string;
  languageModelNames?: Record<string, string>;
}

const handleSelectAllForType = ({
  type,
  filterIndex,
  update,
  watch,
  availableChannels = [],
  desks = [],
  brains = [],
  members = [],
  enabledModelsWithMoveo = [],
  insight,
  searchText = '',
  channelLabel = (channel: string) => channel,
  languageModelNames = {},
}: SelectAllHandler) => {
  const currentFilter = watch(`filters.${filterIndex}`);

  const matches = createSearchMatcher(searchText);
  const visible = <T,>(items: T[], getSearchTerms: (item: T) => string[]) =>
    items.filter((item) => matches(getSearchTerms(item)));

  const selectAllDesks = () => {
    const visibleIds = visible(desks, (desk) => deskSearchTerms(desk)).map((desk) => desk.desk_id);
    update(filterIndex, { ...currentFilter, deskIds: mergeIds(currentFilter.deskIds, visibleIds) });
  };

  const selectAllHandlers: Record<string, () => void> = {
    channels: () =>
      update(filterIndex, {
        ...currentFilter,
        channels: mergeIds(
          currentFilter.channels,
          visible(availableChannels, (channel) => [channelLabel(channel)])
        ),
      }),
    environments: selectAllDesks,
    deskIds: selectAllDesks,
    brainIds: () => {
      const visibleIds = visible(brains, (brain) => brainSearchTerms(brain)).map(
        (brain) => brain.brain_id
      );
      update(filterIndex, {
        ...currentFilter,
        brainIds: mergeIds(currentFilter.brainIds, visibleIds),
      });
    },
    agentIds: () => {
      const nameById = new Map(members.map((member) => [member.user_id, member.name]));
      const visibleAgents = visible(members, (member) => memberSearchTerms(member)).map(
        (member) => ({ value: member.user_id, label: member.name })
      );
      update(filterIndex, {
        ...currentFilter,
        agentIds: mergeAgentIds(
          currentFilter.agentIds,
          visibleAgents,
          (id) => nameById.get(id) ?? id
        ),
      });
    },
    'language-models': () => {
      const visibleIds = visible(enabledModelsWithMoveo, (model) =>
        languageModelSearchTerms(model, languageModelNames)
      ).map((model) => model.language_model_id);
      update(filterIndex, {
        ...currentFilter,
        languageModelIds: mergeIds(currentFilter.languageModelIds, visibleIds),
      });
    },
    // Checkbox filters with finite option sets
    goalAchieved: () => {
      // 1: achieved, 2: partially achieved, 3: failed
      update(filterIndex, { ...currentFilter, goalAchieved: [1, 2, 3] });
    },
    userSentiment: () => {
      // 1: positive, 2: neutral, 3: negative
      update(filterIndex, { ...currentFilter, userSentiment: [1, 2, 3] });
    },
  };

  // Handle insight filters
  if (
    type.startsWith('insight-') &&
    insight?.type === INSIGHT_TYPES.CATEGORICAL &&
    insight?.categories?.length &&
    insight.categories.length > 0
  ) {
    const insightFilters = (currentFilter.insightFilters as InsightFilterType[]) || [];
    const existingFilterIndex = insightFilters.findIndex(
      (filter: InsightFilterType) => filter.id === insight.name
    );

    const newInsightFilter: InsightFilterType = {
      id: insight.name,
      category: insight.type,
      operator: 'in',
      values: [...(insight.categories || [])],
    };

    const updatedInsightFilters = [...insightFilters];
    if (existingFilterIndex !== -1) {
      updatedInsightFilters[existingFilterIndex] = newInsightFilter;
    } else {
      updatedInsightFilters.push(newInsightFilter);
    }

    update(filterIndex, { ...currentFilter, insightFilters: updatedInsightFilters });
    return;
  }

  const handler = selectAllHandlers[type];
  if (handler) {
    handler();
  }
};

const handleSelectNoneForType = ({
  type,
  filterIndex,
  update,
  watch,
  insight,
}: Omit<
  SelectAllHandler,
  'availableChannels' | 'desks' | 'dialogTags' | 'enabledModelsWithMoveo'
>) => {
  const currentFilter = watch(`filters.${filterIndex}`);

  const selectNoneHandlers: Record<string, () => void> = {
    channels: () => update(filterIndex, { ...currentFilter, channels: [] }),
    environments: () => update(filterIndex, { ...currentFilter, deskIds: [] }),
    deskIds: () => update(filterIndex, { ...currentFilter, deskIds: [] }),
    brainIds: () => update(filterIndex, { ...currentFilter, brainIds: [] }),
    agentIds: () => update(filterIndex, { ...currentFilter, agentIds: [] }),
    'language-models': () => update(filterIndex, { ...currentFilter, languageModelIds: [] }),
    goalAchieved: () => update(filterIndex, { ...currentFilter, goalAchieved: [] }),
    userSentiment: () => update(filterIndex, { ...currentFilter, userSentiment: [] }),
  };

  // Handle insight filters
  if (
    type.startsWith('insight-') &&
    insight?.type === INSIGHT_TYPES.CATEGORICAL &&
    insight?.categories?.length &&
    insight.categories.length > 0
  ) {
    const insightFilters = (currentFilter.insightFilters as InsightFilterType[]) || [];
    const existingFilterIndex = insightFilters.findIndex(
      (filter: InsightFilterType) => filter.id === insight.name
    );

    if (existingFilterIndex !== -1) {
      const updatedInsightFilters = insightFilters.filter(
        (filter: InsightFilterType) => filter.id !== insight.name
      );
      update(filterIndex, { ...currentFilter, insightFilters: updatedInsightFilters });
    }
    return;
  }

  const handler = selectNoneHandlers[type];
  if (handler) {
    handler();
  }
};

const shouldShowBulkActions = (type: string, insight?: { type: string; categories?: string[] }) => {
  return (
    FILTER_REGISTRY[type]?.bulkSelectable === true ||
    (type.startsWith('insight-') &&
      insight?.type === INSIGHT_TYPES.CATEGORICAL &&
      insight?.categories?.length > 0)
  );
};

const getFilterLayout = (type: string, insight?: { type: string }): FilterLayout => {
  const registered = FILTER_REGISTRY[type];
  if (registered) {
    return registered.layout;
  }

  if (type.startsWith('insight-') && insight?.type) {
    const isCheckboxInsight =
      insight.type === INSIGHT_TYPES.CATEGORICAL || insight.type === INSIGHT_TYPES.BOOLEAN;
    return isCheckboxInsight ? 'compact' : 'roomy';
  }

  return 'roomy';
};

const CONTENT_PADDING: Record<FilterLayout, string> = {
  compact: 'px-1 pb-2',
  roomy: 'px-3 pt-0 pb-4',
  input: 'px-3 pt-1 pb-4',
};

/**
 * Renders filter dropdown with appropriate children based on filter type
 */
export const SessionFilterDropdown = ({
  type,
  filterIndex,
  onClose,
  onApply,
  onClear: _onClear,
  variant = 'floating',
  className,
  scope = 'brain',
}: SessionFilterDropdownProps) => {
  const { t } = useTranslation();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { control, watch } = useFormContext();
  const availableChannels = useSelector(selectAvailableChannels);
  const [searchText, setSearchText] = useState('');

  // Map UI filter types to data model types for getFilterTitle
  const filterType = type === 'language-models' ? 'languageModelIds' : type;

  const brainId = useSelector(selectBrainId);
  const dialogId = useSelector(selectDialogId);
  const { data: allInsights } = useAllInsights(brainId);
  const { desks } = useDesks();
  const { brains } = useBrains();
  const { members } = useMembers();
  const { dialogTags } = useDialogs(brainId, dialogId);
  const { enabledModelsWithMoveo, languageModelNames } = useLanguageModels();

  // Use useFieldArray to get the update method
  const { update } = useFieldArray({ control, name: 'filters' });

  // Get insight data if this is an insight filter
  const insight = useMemo(() => {
    if (!type.startsWith('insight-') || !allInsights) {
      return null;
    }
    const insightId = type.replace('insight-', '');
    return (
      allInsights.find((i) => i.insight_id === insightId) ||
      allInsights.find((i) => i.name === insightId)
    );
  }, [type, allInsights]);

  // Get title - use insight name for insight filters
  const title = type.startsWith('insight-')
    ? insight?.name || t('analytics.filters.insight')
    : getFilterTitle(filterType, t, scope);

  useEffect(() => {
    /**
     * Handle clicks outside the filter dropdown to close it.
     */
    const handleMouseDown = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        onClose('outside');
      }
    };

    if (variant === 'popover') {
      return undefined;
    }

    document.addEventListener('mousedown', handleMouseDown);
    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
    };
  }, [onClose, variant]);

  const renderFilterContent = (): ReactNode => {
    if (type === 'ratings') {
      return (
        <CsatStarPicker
          filterIndex={filterIndex}
          valueField="ratings"
          modeField="ratingsMode"
          legacyValue={scope === 'account' ? 'threshold' : 'stars'}
        />
      );
    }
    if (type === 'csatBrain') {
      return (
        <CsatStarPicker
          filterIndex={filterIndex}
          valueField="csatBrain"
          modeField="csatBrainMode"
          legacyValue="stars"
        />
      );
    }

    // Handle insight filters
    if (type.startsWith('insight-')) {
      const insightId = type.replace('insight-', '');
      return <InsightFilter filterIndex={filterIndex} insightId={insightId} />;
    }

    // Account scope: use AccountTagsFilter for tags
    if (type === 'tags' && scope === 'account') {
      return <AccountTagsFilter filterIndex={filterIndex} />;
    }

    // Handle standard filters
    const FilterComponent = FILTER_REGISTRY[type]?.Component;
    return FilterComponent ? (
      <FilterComponent
        filterIndex={filterIndex}
        searchText={searchText}
        onSearchChange={setSearchText}
      />
    ) : null;
  };

  const handleSelectAll = () => {
    handleSelectAllForType({
      type,
      filterIndex,
      update,
      watch,
      availableChannels,
      desks,
      brains,
      members,
      dialogTags,
      enabledModelsWithMoveo,
      languageModelNames,
      insight,
      searchText,
      channelLabel: (channel: string) => t(getChannelKey(channel as IntegrationType)),
    });
  };

  const handleSelectNone = () => {
    handleSelectNoneForType({ type, filterIndex, update, watch, insight });
  };

  const filterContent = renderFilterContent();

  if (!filterContent) {
    return null;
  }

  const dropdownWidth = FILTER_REGISTRY[type]?.width ?? DEFAULT_WIDTH;
  const showBulkActions = shouldShowBulkActions(type, insight);
  const layout = getFilterLayout(type, insight);
  const contentPadding = CONTENT_PADDING[layout];
  const isInputOnly = layout === 'input';

  const handleContentKeyDown = (e: React.KeyboardEvent) => {
    if (isKeyEnter(e)) {
      onClose();
    }
  };

  const containerClasses =
    variant === 'popover'
      ? `${dropdownWidth} rounded-xl bg-background shadow-lg`
      : `absolute top-10 right-0 z-50 ${dropdownWidth} mb-10 rounded-xl bg-background shadow-lg`;

  return (
    <div ref={dropdownRef} className={cn(containerClasses, className)}>
      <FilterDropdownWrapper
        title={title}
        onApply={onApply}
        onClose={onClose}
        onSelectAll={showBulkActions ? handleSelectAll : undefined}
        onSelectNone={showBulkActions ? handleSelectNone : undefined}
        showAllNoneButtons={showBulkActions}
        showBackButton={variant !== 'popover'}
      >
        <div
          role={isInputOnly ? 'presentation' : undefined}
          className={`max-h-115 overflow-y-auto ${contentPadding}`}
          onKeyDown={isInputOnly ? handleContentKeyDown : undefined}
        >
          {filterContent}
        </div>
      </FilterDropdownWrapper>
    </div>
  );
};
